import { HelpCircle, Menu, X } from 'lucide-react';
import { useContext, useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { version } from '@/../package.json';
import Logo from '@/components/Logo';
import MapFilters from '@/components/MapFilters/MapFilters';
import GlobalContext from '@/context/GlobalContext';
import { useGetDisciplines } from '@/hooks/queries/useDisciplines';
import { useGetSiteSettings } from '@/hooks/queries/useSiteSettings';
import { useAuth } from '@/hooks/useAuth';
import useOutsideClick from '@/hooks/useOutsideClick';
import { useWindowSize } from '@/hooks/useWindowSize';
import { supabase } from '@/lib/supabase';
import ParentAlignmentIntro from '@/Pages/ParentAlignment/ParentAlignmentIntro';
import MobileParentMapIntro from '@/Pages/ParentMap/MobileParentMapIntro';
import ParentMapIntro from '@/Pages/ParentMap/ParentMapIntro';
import { useAlignmentStore } from '@/stores/alignmentStore';
import { isLocalStorageAvailable } from '@/utils';

import { showErrorToast } from '../ToastNotification';

import './PublicNavbar.css';

const PublicNavbar = ({ isSearchable = false }) => {
	const { user } = useAuth();
	const navigate = useNavigate();
	const { pathname } = useLocation();
	const { siteSettings } = useContext(GlobalContext);
	const avatarRef = useRef<HTMLDivElement>(document.createElement('div'));
	const { data: siteSettingsData, isLoading: siteSettingsLoading } = useGetSiteSettings();
	const { data: disciplines, isLoading: disciplinesLoading } = useGetDisciplines();
	const settingsLoading = siteSettingsLoading || disciplinesLoading;
	const settings = { meta: siteSettingsData, disciplines: disciplines ?? [] };
	const { alignmentCoursesStore } = useAlignmentStore();
	const [isOpen, setIsOpen] = useState(false);
	const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
	const [width] = useWindowSize();

	// biome-ignore lint/correctness/useExhaustiveDependencies: pathname is the trigger, not used inside the effect body
	useEffect(() => {
		setIsMobileMenuOpen(false);
	}, [pathname]);

	const tutorialState = () => {
		if (isLocalStorageAvailable() && pathname === '/') {
			return JSON.parse(localStorage.getItem('elevateCourseAlignmentTutorial') ?? 'true');
		}
		if (isLocalStorageAvailable() && pathname !== '/') {
			return JSON.parse(localStorage.getItem('elevateCourseMapTutorial') ?? 'true');
		}
		return 'true';
	};

	const mobileTutorialState = () => {
		if (isLocalStorageAvailable() && pathname !== '/') {
			return JSON.parse(localStorage.getItem('elevateMobileCourseMapTutorial') ?? 'true');
		}
		return false;
	};

	const [isEnabled, setIsEnabled] = useState(tutorialState());
	const [isMobileEnabled, setIsMobileEnabled] = useState(mobileTutorialState());

	function showTutorial() {
		setIsMobileMenuOpen(false);
		if (isLocalStorageAvailable()) {
			localStorage.setItem('elevateCourseMapTutorial', 'true');
			localStorage.setItem('elevateMobileCourseMapTutorial', 'true');
		}
		// Mobile + Course Map → mobile tutorial; everything else → standard tutorial
		if (width < 900 && pathname !== '/') {
			setIsMobileEnabled(true);
		} else {
			setIsEnabled(true);
		}
	}

	useOutsideClick(avatarRef, () => {
		if (isOpen) setIsOpen(false);
	});

	const signOut = async () => {
		try {
			setIsOpen(false);
			await supabase.auth.signOut();
			if (pathname === '/course-map') {
				navigate('/course-map');
			} else {
				navigate('/');
			}
		} catch (error) {
			showErrorToast('Unable to sign out...please try again.');
		}
	};

	const showCorrectTutorial = () => {
		if (pathname === '/') {
			return (
				alignmentCoursesStore.length > 0 && (
					<ParentAlignmentIntro isEnabled={isEnabled} setIsEnabled={setIsEnabled} />
				)
			);
		}
		return width >= 900 ? (
			<ParentMapIntro isEnabled={isEnabled} setIsEnabled={setIsEnabled} />
		) : (
			<MobileParentMapIntro isEnabled={isMobileEnabled} setIsEnabled={setIsMobileEnabled} />
		);
	};

	const navLinkClass = ({ isActive }: { isActive: boolean }) =>
		`navbar-link text-sm font-semibold rounded px-2 py-1 transition-colors whitespace-nowrap${isActive ? ' active' : ''}`;

	const mobileLinkClass = ({ isActive }: { isActive: boolean }) =>
		`flex items-center px-4 py-4 text-2xl font-semibold border-b border-white/10 transition-colors no-underline${isActive ? ' text-white' : ' text-white/80 hover:text-white'}`;

	return (
		<>
			{showCorrectTutorial()}

			{/* Wrapper creates the stacking context; mobile panel is positioned relative to this */}
			<div className="relative" style={{ zIndex: 20 }}>
				{/* ── Navbar ── */}
				<nav
					className="flex items-center gap-2 bg-white px-4 py-2 condensed:px-6 condensed:py-3"
					style={{ boxShadow: '0 1px 0 rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04)' }}
				>
					{/* Logo — pushes everything right */}
					<div className="mr-auto">
						<Logo logoUrl={siteSettings?.logo ?? ''} />
					</div>

					{/* ── Desktop inline nav (hidden on mobile) ── */}
					<div className="hidden small-screen:flex items-center gap-1">
						{user && (
							<NavLink to="/dashboard/courses" className={navLinkClass}>
								Dashboard
							</NavLink>
						)}
						{pathname === '/' && (
							<NavLink to="/course-map" className={navLinkClass}>
								Course Map
							</NavLink>
						)}
						{pathname !== '/' && (
							<NavLink to="/" end className={navLinkClass}>
								Course Alignment
							</NavLink>
						)}
						{isSearchable && !settingsLoading && (
							<MapFilters dropdownOptions={settings?.disciplines} />
						)}
					</div>

					{/* ── Help — always visible ── */}
					<button
						type="button"
						onClick={showTutorial}
						className="navbar-help-btn flex items-center gap-1 rounded px-2 py-1 text-sm font-semibold transition-colors cursor-pointer bg-transparent border-0"
					>
						<HelpCircle className="w-4 h-4" />
						<span className="hidden small-screen:inline">Help</span>
					</button>

					{/* ── Avatar / Sign-in — desktop only ── */}
					<div className="relative hidden small-screen:block" ref={avatarRef}>
						{user === null && (
							<Link
								to="/login"
								className="navbar-link text-sm font-semibold rounded px-2 py-1 transition-colors whitespace-nowrap"
							>
								Sign In
							</Link>
						)}
						{user !== null && (
							<button
								type="button"
								onClick={() => {
									setIsMobileMenuOpen(false);
									setIsOpen(!isOpen);
								}}
								className="rounded-full border-[3px] border-transparent shrink-0 h-9 w-9 small-screen:h-[42px] small-screen:w-[42px] overflow-hidden p-0 cursor-pointer transition-[border-color] duration-200 hover:border-[rgb(115_114_149/43%)] focus:border-[rgb(115_114_149/43%)] focus:outline-none"
							>
								<div
									className="flex items-center justify-center h-full w-full bg-[#737295] text-white text-sm select-none leading-none"
									style={{ fontFamily: 'var(--font-semibold)' }}
								>
									{user?.email?.split('')[0].toUpperCase()}
								</div>
							</button>
						)}
						{isOpen && (
							<div className="absolute right-0 top-[calc(100%+6px)] bg-white border border-(--input-border) rounded-lg shadow-[0_4px_16px_rgba(0,0,0,0.1)] min-w-[140px] overflow-hidden z-10">
								<button
									type="button"
									onClick={signOut}
									className="w-full text-left px-4 py-2 text-sm bg-transparent border-0 cursor-pointer text-(--label-text) transition-colors hover:bg-(--elevate-primary) hover:text-white focus:bg-(--elevate-primary) focus:text-white focus:outline-none"
									style={{ fontFamily: 'var(--font-semibold)' }}
								>
									Sign Out
								</button>
								<div className="border-t border-(--input-border)" />
								<Link
									to="/privacy-policy"
									onClick={() => setIsOpen(false)}
									className="block px-4 py-2 text-xs text-muted-foreground hover:bg-accent hover:text-foreground no-underline transition-colors"
								>
									Privacy Policy
								</Link>
							</div>
						)}
					</div>

					{/* ── Pill menu button — mobile only ── */}
					<button
						type="button"
						aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
						aria-expanded={isMobileMenuOpen}
						onClick={() => {
							setIsOpen(false);
							setIsMobileMenuOpen(!isMobileMenuOpen);
						}}
						className="small-screen:hidden flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-semibold text-white cursor-pointer border-0 transition-opacity hover:opacity-90 focus:outline-none"
						style={{ backgroundColor: 'var(--org-color)', fontFamily: 'var(--font-semibold)' }}
					>
						{isMobileMenuOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
						<span>{isMobileMenuOpen ? 'Close' : 'Menu'}</span>
					</button>
				</nav>

				{/* ── Mobile menu — outside nav, inside wrapper ── */}
				{isMobileMenuOpen && (
					<>
						{/* biome-ignore lint/a11y/noStaticElementInteractions: backdrop dismiss overlay */}
						<div
							role="presentation"
							className="fixed inset-0 bg-black/40 small-screen:hidden"
							style={{ zIndex: 1 }}
							onClick={() => setIsMobileMenuOpen(false)}
							onKeyDown={() => setIsMobileMenuOpen(false)}
						/>

						{/* Panel — above backdrop, positioned just below the navbar */}
						<div
							className="navbar-panel-animate absolute top-[calc(100%+8px)] left-3 right-3 small-screen:hidden overflow-hidden rounded-2xl max-w-[500px] ml-auto"
							style={{
								zIndex: 2,
								backgroundColor: 'var(--elevate-primary)',
								boxShadow: '0 16px 40px rgba(0,0,0,0.3)',
							}}
						>
							<div className="flex flex-col px-2 py-2">
								{user && (
									<NavLink to="/dashboard/courses" className={mobileLinkClass}>
										Dashboard
									</NavLink>
								)}
								{pathname === '/' ? (
									<NavLink to="/course-map" className={`${mobileLinkClass} border-b-0`}>
										Course Map
									</NavLink>
								) : (
									<NavLink to="/" end className={`${mobileLinkClass} border-b-0`}>
										Course Alignment
									</NavLink>
								)}

								{/* ── User + Help + Sign Out ── */}
								<div className="border-t border-white/10 flex flex-col gap-1 px-2 pt-1">
									{/* User info */}
									{user && (
										<div className="flex items-center gap-3 py-2">
											<div
												className="rounded-full shrink-0 h-10 w-10 overflow-hidden flex items-center justify-center bg-[#737295] text-white text-sm select-none"
												style={{ fontFamily: 'var(--font-semibold)' }}
											>
												{user.email?.split('')[0].toUpperCase()}
											</div>
											<span className="text-sm text-white/60 truncate">{user.email}</span>
										</div>
									)}

									{/* Help + Sign Out row */}
									<div className="flex items-center gap-2 pt-1">
										<button
											type="button"
											onClick={showTutorial}
											className="flex-1 flex items-center justify-center gap-2 rounded-xl py-3 text-sm font-semibold text-white/80 hover:text-white bg-white/10 hover:bg-white/15 border-0 cursor-pointer transition-colors"
											style={{ fontFamily: 'var(--font-semibold)' }}
										>
											<HelpCircle className="w-4 h-4 shrink-0" />
											Help
										</button>
										{user && (
											<button
												type="button"
												onClick={signOut}
												className="flex-1 flex items-center justify-center gap-2 rounded-xl py-3 text-sm font-semibold text-white/80 hover:text-white bg-white/10 hover:bg-white/15 border-0 cursor-pointer transition-colors"
												style={{ fontFamily: 'var(--font-semibold)' }}
											>
												Sign Out
											</button>
										)}
										{user === null && (
											<Link
												to="/login"
												className="flex-1 flex items-center justify-center gap-2 rounded-xl py-3 text-sm font-semibold text-white/80 hover:text-white bg-white/10 hover:bg-white/15 no-underline transition-colors"
											>
												Sign In
											</Link>
										)}
									</div>

									{/* Privacy + copyright */}
									<div className="flex items-center justify-between pt-3 pb-1 px-2">
										<span className="text-[11px] text-white/30">
											© {new Date().getFullYear()} Stakk IO · v{version}
										</span>
										<Link
											to="/privacy-policy"
											className="text-[11px] text-white/50 hover:text-white/80 no-underline transition-colors"
											onClick={() => setIsMobileMenuOpen(false)}
										>
											Privacy Policy
										</Link>
									</div>
								</div>
							</div>
						</div>
					</>
				)}
			</div>
		</>
	);
};

export default PublicNavbar;
